import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Amplify } from "aws-amplify";
import { ConfirmProvider } from "material-ui-confirm";
import { UploadProvider } from "./contexts/UploadContext";
import { readCurrentUser, signIn, updateCurrentUser } from "./store/auth.slice";
import LayoutContextProvider from "./contexts/LayoutContext";
import AppContextProvider from "./contexts/AppContext";
import MainRoutes from "./routes/Routes";
import config from "./config";
import {
  hasStoredAuthToken,
  getAuthUserIdFromToken,
  getAuthProfileFromToken,
  getAuthEmailFromToken,
  resolveLoginUrl,
} from "./utils/authSession";

// Keep Amplify configured for S3 Storage (used by UploadContext)
Amplify.configure(config.AWS);

const App = () => {
  const dispatch = useDispatch();
  const auth = useSelector((store) => store.auth);

  const hasStoredToken = hasStoredAuthToken();
  const shouldRedirectToLogin = !auth?.authenticated && !hasStoredToken;

  // Redirect to login app when no token found
  useEffect(() => {
    if (!shouldRedirectToLogin) return;
    const loginUrl = resolveLoginUrl();
    if (loginUrl) {
      window.location.replace(loginUrl);
    }
  }, [shouldRedirectToLogin]);

  // Hydrate auth state from stored token
  useEffect(() => {
    if (!hasStoredToken) return;
    if (auth?.authenticated && auth?.PrivateInfo) return;

    const tokenProfile = getAuthProfileFromToken();
    const tokenEmail =
      tokenProfile?.email ||
      tokenProfile?.preferredUsername ||
      tokenProfile?.name ||
      getAuthEmailFromToken();
    const tokenUserId =
      getAuthUserIdFromToken() ||
      tokenEmail ||
      "";

    if (!tokenUserId) return;

    if (!auth?.authenticated) {
      dispatch(
        signIn({
          id: tokenUserId,
          name: tokenEmail || tokenUserId,
          role: "creator",
        })
      );
    }
  }, [hasStoredToken, auth?.authenticated, auth?.PrivateInfo, dispatch]);

  // Fetch user from backend once authenticated
  useEffect(() => {
    if (auth.userId && auth.authenticated) {
      dispatch(readCurrentUser(auth.cognitoUser));
    }
  }, [auth.userId, auth.authenticated, dispatch]);

  return (
    <UploadProvider>
      <LayoutContextProvider>
        <AppContextProvider>
          <ConfirmProvider
            defaultOptions={{
              confirmationButtonProps: {
                color: "primary",
                variant: "contained",
              },
            }}
          >
            <MainRoutes></MainRoutes>
          </ConfirmProvider>
        </AppContextProvider>
      </LayoutContextProvider>
    </UploadProvider>
  );
};

export default App;
